import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Asset } from '@/lib/api/types';
import { api, req } from '@/lib/api/client';
import { keys } from '@/lib/api/keys';
import { MEDIA_URL_STALE_MS } from '@/config/constants';
import { useScrollContainer } from '@/features/grid/ScrollContainer';

/** At most this many list rows fetch their asset detail at once. */
export const ROW_DETAIL_CONCURRENCY = 4;
/** A row must stay in view this long before it asks for its detail, so scrolling past rows costs nothing. */
export const ROW_DETAIL_DWELL_MS = 150;

/** FIFO job queue with a cap on running jobs; a job whose signal aborts while it waits is dropped, never started. */
export function createLimiter(max: number) {
  let running = 0;
  const queue: Array<{ key: string; start: () => void }> = [];
  const pump = () => {
    while (running < max && queue.length) {
      running++;
      queue.shift()!.start();
    }
  };
  return {
    run<T>(key: string, fn: () => Promise<T>, signal?: AbortSignal): Promise<T> {
      return new Promise<T>((resolve, reject) => {
        if (signal?.aborted) return reject(signal.reason);
        const onAbort = () => {
          const i = queue.indexOf(job);
          if (i < 0) return;
          queue.splice(i, 1);
          reject(signal!.reason);
        };
        const job = {
          key,
          start: () => {
            signal?.removeEventListener('abort', onAbort);
            fn()
              .then(resolve, reject)
              .finally(() => {
                running--;
                pump();
              });
          },
        };
        signal?.addEventListener('abort', onAbort, { once: true });
        queue.push(job);
        pump();
      });
    },
    queued: (key: string) => queue.some((j) => j.key === key),
    get running() {
      return running;
    },
  };
}

const limiter = createLimiter(ROW_DETAIL_CONCURRENCY);

/**
 * Asset detail for one list row (custom field values and `effective_mask` are not on the card, and no operation
 * reads them for many assets at once). Fetched only while the row has been in view for a moment, through a shared
 * queue capped at {@link ROW_DETAIL_CONCURRENCY}; a row that leaves the view before its turn drops its request.
 * Shares the `keys.asset` cache with the detail panel.
 */
export function useRowDetail(ref: React.RefObject<HTMLElement | null>, assetId: string, enabled: boolean): Asset | null {
  const want = useDwellInView(ref, enabled);
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: keys.asset(assetId),
    queryFn: ({ signal }) => limiter.run(assetId, () => req(api.GET('/assets/{assetId}', { params: { path: { assetId } } })) as Promise<Asset>, signal),
    enabled: want,
    staleTime: MEDIA_URL_STALE_MS,
  });
  useEffect(() => {
    if (!want && limiter.queued(assetId)) void qc.cancelQueries({ queryKey: keys.asset(assetId), exact: true });
  }, [want, assetId, qc]);
  return q.data ?? null;
}

/** True while the element is near the viewport and has been for {@link ROW_DETAIL_DWELL_MS}. */
function useDwellInView(ref: React.RefObject<HTMLElement | null>, enabled: boolean): boolean {
  const root = useScrollContainer();
  const [want, setWant] = useState(false);
  useEffect(() => {
    if (!enabled || !ref.current) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const seen = (visible: boolean) => {
      clearTimeout(timer);
      if (visible) timer = setTimeout(() => setWant(true), ROW_DETAIL_DWELL_MS);
      else setWant(false);
    };
    if (typeof IntersectionObserver === 'undefined') {
      seen(true);
      return () => clearTimeout(timer);
    }
    const io = new IntersectionObserver((es) => seen(es.some((e) => e.isIntersecting)), { root, rootMargin: '200px' });
    io.observe(ref.current);
    return () => {
      io.disconnect();
      clearTimeout(timer);
    };
  }, [enabled, ref, root]);
  return enabled && want;
}
